import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";

interface MapPickerWithRadiusProps {
  lat?: number;
  lng?: number;
  radiusMiles?: number;
  onLocationSelect: (lat: number, lng: number) => void;
  onRadiusChange?: (radiusMiles: number) => void;
  apiKey?: string;
}

export function MapPickerWithRadius({ 
  lat, 
  lng, 
  radiusMiles,
  onLocationSelect, 
  onRadiusChange,
  apiKey 
}: MapPickerWithRadiusProps) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // Load Google Maps script
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    const script = document.createElement("script");
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyCtNluNHfcLUew99csGU9o593Iv79u9dP0&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);

    return () => {
      // Cleanup script if component unmounts before loading
      if (script.parentNode) {
        script.parentNode.removeChild(script);
      }
    };
  }, [apiKey]);

  useEffect(() => {
    if (!isLoaded || !mapRef.current || !window.google?.maps || mapInstanceRef.current) return;

    const initialLat = lat || 0;
    const initialLng = lng || 0;
    const center = lat && lng 
      ? { lat: initialLat, lng: initialLng }
      : { lat: 40.7128, lng: -74.0060 }; // Default to New York

    // Initialize map
    const map = new google.maps.Map(mapRef.current, {
      center,
      zoom: lat && lng ? 15 : 2,
      mapTypeControl: true,
      streetViewControl: true,
      fullscreenControl: true,
    });

    mapInstanceRef.current = map;

    // Initialize Places Autocomplete
    if (searchInputRef.current && window.google.maps.places) {
      const autocomplete = new google.maps.places.Autocomplete(searchInputRef.current, {
        types: ['geocode'],
        fields: ['geometry', 'formatted_address'],
      });

      autocompleteRef.current = autocomplete;

      // Handle place selection
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        
        if (place.geometry?.location) {
          const newLat = place.geometry.location.lat();
          const newLng = place.geometry.location.lng();
          
          // Update map center
          map.setCenter({ lat: newLat, lng: newLng });
          map.setZoom(15);
          
          // Update or create marker
          if (markerRef.current) {
            markerRef.current.setPosition({ lat: newLat, lng: newLng });
          } else {
            const marker = new google.maps.Marker({
              position: { lat: newLat, lng: newLng },
              map,
              draggable: true,
            });
            markerRef.current = marker;

            // Update coordinates when marker is dragged
            marker.addListener("dragend", () => {
              const position = marker.getPosition();
              if (position) {
                const draggedLat = position.lat();
                const draggedLng = position.lng();
                onLocationSelect(draggedLat, draggedLng);
                
                // Update circle center if it exists
                if (circleRef.current) {
                  circleRef.current.setCenter({ lat: draggedLat, lng: draggedLng });
                }
              }
            });
          }

          // Update circle center if it exists
          if (circleRef.current) {
            circleRef.current.setCenter({ lat: newLat, lng: newLng });
          }

          onLocationSelect(newLat, newLng);
        }
      });
    }

    // Add marker if initial coordinates exist
    if (lat && lng) {
      const marker = new google.maps.Marker({
        position: { lat: initialLat, lng: initialLng },
        map,
        draggable: true,
      });
      markerRef.current = marker;

      // Update coordinates when marker is dragged
      marker.addListener("dragend", () => {
        const position = marker.getPosition();
        if (position) {
          const newLat = position.lat();
          const newLng = position.lng();
          onLocationSelect(newLat, newLng);
          
          // Update circle center if it exists
          if (circleRef.current) {
            circleRef.current.setCenter({ lat: newLat, lng: newLng });
          }
        }
      });
    }

    // Add click listener to map
    map.addListener("click", (e: google.maps.MapMouseEvent) => {
      if (e.latLng) {
        const clickedLat = e.latLng.lat();
        const clickedLng = e.latLng.lng();
        
        // Update or create marker
        if (markerRef.current) {
          markerRef.current.setPosition({ lat: clickedLat, lng: clickedLng });
        } else {
          const marker = new google.maps.Marker({
            position: { lat: clickedLat, lng: clickedLng },
            map,
            draggable: true,
          });
          markerRef.current = marker;

          // Update coordinates when marker is dragged
          marker.addListener("dragend", () => {
            const position = marker.getPosition();
            if (position) {
              const newLat = position.lat();
              const newLng = position.lng();
              onLocationSelect(newLat, newLng);
              
              // Update circle center if it exists
              if (circleRef.current) {
                circleRef.current.setCenter({ lat: newLat, lng: newLng });
              }
            }
          });
        }

        onLocationSelect(clickedLat, clickedLng);
      }
    });
  }, [isLoaded, onLocationSelect]);

  // Update marker position when lat/lng change externally
  useEffect(() => {
    if (mapInstanceRef.current && markerRef.current && lat && lng) {
      const position = { lat, lng };
      markerRef.current.setPosition(position);
      mapInstanceRef.current.setCenter(position);
      
      // Update circle center if it exists
      if (circleRef.current) {
        circleRef.current.setCenter(position);
      }
    } else if (mapInstanceRef.current && !lat && !lng && markerRef.current) {
      // Remove marker if coordinates are cleared
      markerRef.current.setMap(null);
      markerRef.current = null;
      
      // Remove circle if it exists
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    }
  }, [lat, lng]);

  // Update circle radius when radiusMiles changes
  useEffect(() => {
    if (!mapInstanceRef.current || !markerRef.current || !lat || !lng) {
      // Remove circle if no location is selected
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
      return;
    }

    if (radiusMiles && radiusMiles > 0) {
      const radiusMeters = radiusMiles * 1609.34; // Convert miles to meters
      
      if (circleRef.current) {
        // Update existing circle
        circleRef.current.setRadius(radiusMeters);
      } else {
        // Create new circle
        const circle = new google.maps.Circle({
          strokeColor: "#FF0000",
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: "#FF0000",
          fillOpacity: 0.35,
          map: mapInstanceRef.current,
          center: { lat, lng },
          radius: radiusMeters,
        });
        circleRef.current = circle;
      }
    } else {
      // Remove circle if radius is 0 or undefined
      if (circleRef.current) {
        circleRef.current.setMap(null);
        circleRef.current = null;
      }
    }
  }, [radiusMiles, lat, lng]);

  if (!isLoaded) {
    return (
      <div className="w-full h-96 bg-muted flex items-center justify-center rounded-md">
        <p className="text-muted-foreground">Loading map...</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-2">
      <div className="space-y-2">
        <Input
          ref={searchInputRef}
          type="text"
          placeholder="Search for a location..."
          className="w-full"
        />
      </div>
      <div ref={mapRef} className="w-full h-96 rounded-md border" />
    </div>
  );
}
